import { useState } from 'react';
import { Field, Textarea } from '@/ui';
import { usersApi } from '@/api';
import type { User } from '@/types';
import { StepLayout } from '../StepLayout';

interface AgreementStepProps {
  profile: User;
  onSaved: (user: User) => void;
  onSkip: () => void;
}

export function AgreementStep({ profile, onSaved, onSkip }: AgreementStepProps) {
  const [text, setText] = useState(profile.agreementTemplate ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNext = async () => {
    // Empty body? Skip — don't send a write that nulls out an existing template.
    if (!text.trim()) {
      onSkip();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const resp = await usersApi.updateProfile({ agreementTemplate: text });
      if (resp.success && resp.data) {
        onSaved(resp.data);
      } else {
        setError(resp.error || 'Failed to save.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepLayout
      title="Pre-inspection agreement"
      subtitle="Paste the standard agreement you send clients to sign before each inspection. We'll fill in client and property details automatically when you send it."
      onPrimary={handleNext}
      primaryLabel="Save & continue"
      primaryLoading={saving}
      onSecondary={onSkip}
      secondaryLabel="Skip for now"
      error={error}
    >
      <Field
        label="Agreement text"
        hint="You can edit this any time in Settings. Markdown is supported."
      >
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`This Pre-Inspection Agreement is made between {{inspector_name}} and {{client_name}} for an inspection at {{property_address}} on {{inspection_date}}…`}
          rows={14}
          className="font-mono text-body-sm"
        />
      </Field>
    </StepLayout>
  );
}
